import { useMemo, useState } from 'react';
import { AvisoTope } from '../componentes/AvisoTope';
import { Bloque, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import { EtiquetaConductor, tipoVehiculoTexto, tonoRealDe } from '../componentes/EtiquetaConductor';
import { haceCuanto } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import {
  TOPE_FILAS_CONDUCTORES,
  obtenerConductores,
  type ConductorListado,
  type FiltroConductores,
} from '../dominio/conductoresApi';
import { puede } from '../sesion/permisos';
import { useSesionActiva } from '../sesion/sesion';

/**
 * Las cuatro vistas del selector «Mostrar».
 *
 * 🔑 **Tres de las cuatro se resuelven EN EL BACKEND, una no, y la diferencia importa.** El
 * endpoint acepta `estadoCuenta`, `estadoDisponibilidad` y `estadoVerificacion`, así que
 * «disponibles» y «suspendidos» viajan como query y el tope de 200 se aplica al conjunto YA
 * filtrado. `sin-suscripcion` no tiene filtro server-side (el endpoint no lo ofrece) ⇒ se filtra
 * client-side sobre lo que llegó, con el límite que el aviso de tope explica en pantalla.
 */
type Filtro = 'todos' | 'disponibles' | 'suspendidos' | 'sin-suscripcion';

/** Qué se le pide al backend para cada vista. `{}` = sin filtro. */
const consultaDe = (filtro: Filtro): FiltroConductores => {
  switch (filtro) {
    case 'disponibles':
      return { estadoDisponibilidad: 'DISPONIBLE' };
    case 'suspendidos':
      return { estadoCuenta: 'SUSPENDIDA' };
    default:
      // `sin-suscripcion` y `todos` piden todo: el primero porque el backend no sabe filtrar por
      // ausencia de suscripción, el segundo porque no hay nada que acotar.
      return {};
  }
};

/** Lo que el backend NO puede filtrar y queda para el cliente. */
const aplicaClientSide = (conductor: ConductorListado, filtro: Filtro): boolean =>
  filtro === 'sin-suscripcion' ? conductor.suscripcion?.estado !== 'ACTIVA' : true;

export function Conductores() {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  // `filtro` va en las deps porque ES un parámetro de la consulta (cambia la URL que se pide) —
  // que es justo lo que la cabecera de `useCargaReal` pide poner ahí, y nunca el resultado.
  const [carga, recargar] = useCargaReal(() => obtenerConductores(consultaDe(filtro)), [filtro]);

  const sesion = useSesionActiva();
  const [texto, setTexto] = useState('');
  const ahora = new Date();

  const listos = carga.estado === 'listo' ? carga.datos : [];
  const enElTope = listos.length >= TOPE_FILAS_CONDUCTORES;

  const resultados = useMemo(() => {
    const buscado = texto.trim().toLowerCase();
    return listos.filter(
      (conductor) =>
        aplicaClientSide(conductor, filtro) &&
        // ⚠️ `nombre` y `telefono` son NULLABLE en el backend real (a diferencia del mock, donde
        // eran `string` siempre): sin estas guardas, un conductor sin nombre rompe la búsqueda
        // entera con un TypeError, no sólo su propia fila.
        (buscado === '' ||
          (conductor.nombre?.toLowerCase().includes(buscado) ?? false) ||
          (conductor.telefono?.includes(buscado) ?? false) ||
          (conductor.vehiculo?.placa.toLowerCase().includes(buscado) ?? false) ||
          conductor.id.toLowerCase().includes(buscado)),
    );
  }, [listos, texto, filtro]);

  const puedeSuspender = sesion !== null && puede(sesion.rol, 'suspender.conductor');

  /**
   * 🔴 **Suspender y reactivar NO tienen endpoint.** `consola/conductores` expone un solo `@Get`;
   * el barrido de los controllers de `apps/api` no encontró ninguna ruta que cambie
   * `estadoCuenta`. La versión mock de esta pantalla abría un diálogo de motivo cuyo «Confirmar»
   * sólo cerraba el diálogo — inocuo sobre datos de ejemplo, **peligroso sobre datos reales**:
   * quien lo usara se iría creyendo que suspendió a alguien. Los botones quedan visibles y
   * deshabilitados, diciendo por qué; el diálogo se retiró junto con la acción que no existía.
   */
  const motivoDeshabilitado = puedeSuspender
    ? 'Todavía no hay endpoint en el backend para esta acción: no se puede ejecutar desde acá.'
    : 'Sólo un administrador puede cambiar el estado de una cuenta.';

  return (
    <Pagina
      rotulo="Conductores"
      titulo="Buscar conductores"
      ayuda="Datos reales del backend. Se busca por nombre, teléfono, placa o código; el filtro de estado se resuelve en el servidor."
    >
      <div className="bloque">
        <div className="barra-filtros">
          <label className="campo">
            <span className="campo__etiqueta rotulo">Buscar</span>
            <input
              className="entrada"
              type="search"
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
              placeholder="Nombre, placa, teléfono…"
            />
          </label>
          <label className="campo">
            <span className="campo__etiqueta rotulo">Mostrar</span>
            <select
              className="selector"
              value={filtro}
              onChange={(evento) => setFiltro(evento.target.value as Filtro)}
            >
              <option value="todos">Todos</option>
              <option value="disponibles">Disponibles ahora</option>
              <option value="sin-suscripcion">Sin suscripción activa</option>
              <option value="suspendidos">Suspendidos</option>
            </select>
          </label>
          <span className="tenue">
            {resultados.length} de {listos.length}
            {enElTope ? ' (tope)' : ''}
          </span>
        </div>

        {carga.estado === 'listo' && enElTope ? (
          <AvisoTope
            tope={TOPE_FILAS_CONDUCTORES}
            sugerencia="Acotá con «Mostrar»: ese filtro sí viaja al servidor y hace que el tope se aplique al conjunto ya filtrado."
          />
        ) : null}

        {carga.estado === 'cargando' ? (
          <Cargando filas={5} etiqueta="Cargando conductores" />
        ) : carga.estado === 'error' ? (
          <ConError detalle={carga.mensaje} alReintentar={recargar} />
        ) : resultados.length === 0 ? (
          <Vacio
            titulo="Ningún conductor coincide"
            detalle="Probá con menos filtros, o buscá por la placa completa."
          />
        ) : (
          <Tabla
            cabeceras={[
              'Conductor',
              'Vehículo',
              'Expediente',
              'Disponibilidad',
              'Suscripción',
              { texto: 'Viajes', num: true },
              'Último viaje',
              'Cuenta',
              '',
            ]}
          >
            {resultados.map((conductor) => (
              <tr key={conductor.id} data-estado={tonoRealDe('cuenta', conductor.estadoCuenta)}>
                <td>
                  {/*
                    `nombre` nullable: un conductor sin nombre cargado —o con la cuenta
                    despersonalizada por baja— existe y hay que poder verlo. El id siempre está,
                    así que la fila nunca queda sin identificar.
                  */}
                  {conductor.nombre ?? <span className="apagado">sin nombre</span>}
                  <div className="apagado">
                    {conductor.id}
                    {conductor.telefono ? ` · ${conductor.telefono}` : ''}
                  </div>
                </td>
                <td>
                  {conductor.vehiculo ? (
                    <>
                      {conductor.vehiculo.placa}
                      <div className="apagado">{tipoVehiculoTexto(conductor.vehiculo.tipo)}</div>
                    </>
                  ) : (
                    <span className="apagado">—</span>
                  )}
                </td>
                <td>
                  <EtiquetaConductor tipo="verificacion" valor={conductor.estadoVerificacion} />
                </td>
                <td>
                  <EtiquetaConductor tipo="disponibilidad" valor={conductor.estadoDisponibilidad} />
                </td>
                <td>
                  {conductor.suscripcion ? (
                    <EtiquetaConductor tipo="suscripcion" valor={conductor.suscripcion.estado} />
                  ) : (
                    <span className="apagado">sin suscripción</span>
                  )}
                </td>
                <td className="tabla__num">{conductor.viajesTotales}</td>
                <td className="tenue">{haceCuanto(conductor.ultimoViajeFinAt, ahora)}</td>
                <td>
                  {/*
                    ⚠️ Sin «motivo de la suspensión»: `Conductor` no tiene esa columna en el schema
                    real ni existe un historial de suspensiones. El mock lo mostraba; inventarlo
                    acá sería peor que no tenerlo (ver `dominio/conductoresApi.ts`).
                  */}
                  <EtiquetaConductor tipo="cuenta" valor={conductor.estadoCuenta} />
                </td>
                <td>
                  <button
                    type="button"
                    className={`boton boton--chico${
                      conductor.estadoCuenta === 'SUSPENDIDA' ? '' : ' boton--peligro'
                    }`}
                    disabled
                    title={motivoDeshabilitado}
                  >
                    {conductor.estadoCuenta === 'SUSPENDIDA' ? 'Reactivar' : 'Suspender'}
                  </button>
                </td>
              </tr>
            ))}
          </Tabla>
        )}
      </div>

      <Bloque titulo="Qué se puede y qué no desde acá">
        <p className="pagina__ayuda" style={{ marginTop: 0 }}>
          Esta pantalla <strong>lee</strong> el padrón de conductores. Suspender y reactivar todavía
          no tienen endpoint en el backend: los botones están a la vista para que se sepa de qué
          acciones se trata y quién podría hacerlas, pero no ejecutan nada. Cuando existan, la
          cuenta sale del reparto de inmediato y el viaje en curso <em>sigue</em> — cortarlo dejaría
          a un pasajero en la calle. La suspensión no toca la suscripción: si sigue cobrándose, eso
          se resuelve en <strong>Suscripciones</strong>, y es una decisión distinta.
        </p>
        <p className="pagina__ayuda">
          ⚠️ El listado incluye a quienes tienen la cuenta{' '}
          <strong>dada de baja en el perfil de conductor</strong>, y eso se ve en la columna
          «Cuenta». Lo que la consola <em>no</em> puede ver hoy es la baja del <em>usuario</em>
          (despersonalización): el endpoint no la devuelve, y no es el mismo campo — un usuario
          despersonalizado puede seguir figurando con la cuenta activa. Un nombre vacío no alcanza
          para distinguirlo: también aparece en quien nunca cargó nombre.
        </p>
      </Bloque>
    </Pagina>
  );
}
